/**
 * Platform Slot Component - Fixed Canvas Pattern
 *
 * Always-visible card with fixed 188px height for each platform.
 * Implements Inara's Fixed Canvas Pattern design with three zones:
 * - Header Zone (48px): Icon + Platform Name + Actions Menu
 * - Status Zone (60px): Badge + Context Line (gray background)
 * - Action Zone (40px): Primary action button
 *
 * Display States (from computeDisplayState):
 * - not_connected: No connection exists - gray icon, "Connect to {Platform}" button
 * - connecting: OAuth flow in progress - spinner, "Please wait..."
 * - Connected (green): Active + valid credentials - checkmark badge, "Test Connection"
 * - Connected (blue): Active + refreshing credentials - sync icon, "Test Connection"
 * - Needs Attention (orange): Expired credentials - warning badge, "Reconnect Now"
 * - Setup Incomplete (gray): Pending auth - gray icon, "Complete Setup"
 * - Paused (yellow): User paused syncing - pause icon, "Resume Syncing"
 * - Archived (gray): Long-term inactive - inbox icon, "Restore Connection"
 * - Removed (gray): Soft deleted - shouldn't appear in normal views
 *
 * Based on Inara's Fixed Canvas Pattern redesign (Dec 2025).
 */

import { useState } from 'react'
import { Card, Button, Typography, Badge, Spin } from 'antd'
import {
  CheckCircleFilled,
  WarningFilled,
  LoadingOutlined,
  PauseCircleFilled,
  InboxOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import type { Connection, ConnectionAction, Platform } from '@/types/connection.types'
import type { PlatformConfig } from '@/utils/platformConfig'
import { useCreateConnection, useInitiateOAuth } from '@/hooks'
import { generatePKCEPair } from '@/utils/pkce'
import { computeDisplayState } from '@/utils/connectionState'
import { ConnectionActionsMenu } from './ConnectionActionsMenu'

const { Text } = Typography

/**
 * Props for PlatformSlot component
 */
export interface PlatformSlotProps {
  /** Platform configuration */
  platformConfig: PlatformConfig

  /** Environment (production or sandbox) */
  environment: 'production' | 'sandbox'

  /** Existing connection for this platform+environment (if any) */
  connection: Connection | null

  /** Callback when action is selected */
  onAction: (action: ConnectionAction, connection: Connection) => void

  /** Loading state (disables actions) */
  loading?: boolean
}

/**
 * Platform Slot
 *
 * Displays connection state for a single platform+environment combination.
 * Handles one-click OAuth flow initiation.
 *
 * States:
 * - not_connected: No connection exists → Show [Connect] button
 * - connecting: OAuth flow initiated → Show spinner with 1-sec delay before redirect
 * - connected: Valid connection → Show status, last sync, [Manage] dropdown
 * - error: Connection exists but has errors → Show error message and [Retry] button
 *
 * @example
 * ```tsx
 * <PlatformSlot
 *   platformConfig={PLATFORM_CONFIGS.square}
 *   environment="production"
 *   connection={existingConnection}
 *   onAction={(action, conn) => handleAction(action, conn)}
 * />
 * ```
 */
export const PlatformSlot: React.FC<PlatformSlotProps> = ({
  platformConfig,
  environment,
  connection,
  onAction,
  loading = false,
}) => {
  const [isConnecting, setIsConnecting] = useState(false)

  const createConnection = useCreateConnection()
  const initiateOAuth = useInitiateOAuth()

  // Determine platform value based on environment
  const platformValue: Platform =
    environment === 'sandbox' ? platformConfig.sandboxPlatform : platformConfig.platform

  // Compute display state if connection exists
  const displayState = connection ? computeDisplayState(connection) : null

  // Check if connection needs attention (Needs Attention state)
  const needsAttention = displayState && displayState.label === 'Needs Attention'

  // Handle Connect button click - initiate OAuth flow
  const handleConnect = async () => {
    try {
      setIsConnecting(true)

      // Create connection
      const newConnection = await createConnection.mutateAsync({
        name: `${platformConfig.displayName} ${environment === 'sandbox' ? 'Sandbox' : 'Production'} Connection`,
        platform: platformValue,
        description: `Connected via Rocket Pricing`,
      })

      // Generate PKCE parameters client-side (RFC 7636 compliant)
      const { codeVerifier, codeChallenge } = await generatePKCEPair()

      // Initiate OAuth flow
      const oauthResult = await initiateOAuth.mutateAsync({
        connectionUid: newConnection.uid,
        redirectUri: `${window.location.origin}/oauth/callback`,
        codeChallenge: codeChallenge,
      })

      // Store CSRF state and PKCE code_verifier for validation on callback
      sessionStorage.setItem('oauth_state', oauthResult.state)
      sessionStorage.setItem('oauth_code_verifier', codeVerifier)
      sessionStorage.setItem('oauth_connection_uid', newConnection.uid)

      // Show brief "Connecting..." message before redirect (1-sec delay for UX)
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Redirect to platform OAuth page
      window.location.href = oauthResult.authorizationUrl
    } catch (error) {
      // Error already shown via mutation onError
      console.error('Failed to connect platform:', error)
      setIsConnecting(false)
    }
  }

  // Handle Retry button click (for "Needs Attention" state)
  const handleRetry = () => {
    if (!connection) return

    // For "Needs Attention" state, always reconnect
    onAction('reconnect', connection)
  }

  // Format last sync date (e.g., "Dec 8, 2025")
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // Render platform icon (40px circle with letter, overlay badges for state)
  const renderIcon = () => {
    if (!displayState) {
      // Not connected state - gray icon
      return (
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'var(--rp-border-subtle)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 'bold',
            flexShrink: 0,
          }}
        >
          {platformConfig.name.charAt(0).toUpperCase()}
        </div>
      )
    }

    // Determine icon color based on state
    const isActive = displayState.label === 'Connected' || displayState.label === 'Needs Attention'
    const iconColor = isActive ? platformConfig.color : 'var(--rp-border-subtle)'

    // Determine badge overlay based on state
    let overlayBadge = null
    const badgeStyles = {
      position: 'absolute' as const,
      bottom: '-2px',
      right: '-2px',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid white',
    }

    switch (displayState.label) {
      case 'Connected':
        // Green checkmark for valid, blue sync for refreshing
        overlayBadge = displayState.color === 'blue' ? (
          <div style={{ ...badgeStyles, backgroundColor: 'var(--rp-blue-primary)' }}>
            <SyncOutlined spin style={{ fontSize: '10px', color: '#fff' }} />
          </div>
        ) : (
          <div style={{ ...badgeStyles, backgroundColor: 'var(--rp-success)' }}>
            <CheckCircleFilled style={{ fontSize: '10px', color: '#fff' }} />
          </div>
        )
        break
      case 'Needs Attention':
        overlayBadge = (
          <div style={{ ...badgeStyles, backgroundColor: 'var(--rp-temporal-orange)' }}>
            <WarningFilled style={{ fontSize: '10px', color: '#fff' }} />
          </div>
        )
        break
      case 'Paused':
        overlayBadge = (
          <div style={{ ...badgeStyles, backgroundColor: 'var(--rp-temporal-orange)' }}>
            <PauseCircleFilled style={{ fontSize: '10px', color: '#fff' }} />
          </div>
        )
        break
      case 'Archived':
        overlayBadge = (
          <div style={{ ...badgeStyles, backgroundColor: 'var(--rp-gray-500)' }}>
            <InboxOutlined style={{ fontSize: '10px', color: '#fff' }} />
          </div>
        )
        break
      // Setup Incomplete and Removed don't get badges - just gray icon
    }

    return (
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: iconColor,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          fontWeight: 'bold',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        {platformConfig.name.charAt(0).toUpperCase()}
        {overlayBadge}
      </div>
    )
  }

  // STATE: not_connected - Fixed Canvas Pattern
  if (!connection) {
    return (
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          height: '188px',
        }}
        bodyStyle={{
          padding: '16px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Zone (48px) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '48px',
          marginBottom: '12px',
        }}>
          {renderIcon()}
          <Text strong style={{ fontSize: '16px', flex: 1 }}>
            {platformConfig.displayName}
          </Text>
          {/* No actions menu when not connected */}
          <div style={{ width: '20px' }} />
        </div>

        {/* Status Zone (60px) */}
        <div style={{
          backgroundColor: 'var(--rp-surface-tertiary)',
          borderRadius: '4px',
          padding: '12px',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: 'auto',
        }}>
          <Badge
            status="default"
            text={<Text type="secondary" style={{ fontSize: '14px' }}>Not Connected</Text>}
            style={{ display: 'block', marginBottom: '4px' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Connect to sync pricing
          </Text>
        </div>

        {/* Action Zone (40px) */}
        <Button
          type="primary"
          onClick={handleConnect}
          loading={isConnecting}
          disabled={loading || isConnecting}
          block
          style={{ height: '40px', marginTop: '12px' }}
        >
          Connect to {platformConfig.displayName}
        </Button>
      </Card>
    )
  }

  // STATE: connecting - Fixed Canvas Pattern
  if (isConnecting) {
    return (
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          height: '188px',
        }}
        bodyStyle={{
          padding: '16px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Zone (48px) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '48px',
          marginBottom: '12px',
        }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: platformConfig.color }} spin />} />
          </div>
          <Text strong style={{ fontSize: '16px', flex: 1 }}>
            {platformConfig.displayName}
          </Text>
          {/* No actions menu when connecting */}
          <div style={{ width: '20px' }} />
        </div>

        {/* Status Zone (60px) */}
        <div style={{
          backgroundColor: 'var(--rp-surface-tertiary)',
          borderRadius: '4px',
          padding: '12px',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: 'auto',
        }}>
          <Badge
            status="processing"
            text={<Text style={{ fontSize: '14px', color: '#1890ff' }}>Connecting...</Text>}
            style={{ display: 'block', marginBottom: '4px' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Opening authorization...
          </Text>
        </div>

        {/* Action Zone (40px) */}
        <Button
          type="default"
          disabled
          block
          style={{ height: '40px', marginTop: '12px' }}
        >
          Please wait...
        </Button>
      </Card>
    )
  }

  // STATE: needs attention - Fixed Canvas Pattern
  if (needsAttention) {
    return (
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          height: '188px',
          borderColor: '#fa8c16',
        }}
        bodyStyle={{
          padding: '16px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Zone (48px) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '48px',
          marginBottom: '12px',
        }}>
          {renderIcon()}
          <Text strong style={{ fontSize: '16px', flex: 1 }}>
            {platformConfig.displayName}
          </Text>
          <ConnectionActionsMenu
            connection={connection}
            onAction={onAction}
            disabled={loading}
          />
        </div>

        {/* Status Zone (60px) */}
        <div style={{
          backgroundColor: 'var(--rp-surface-tertiary)',
          borderRadius: '4px',
          padding: '12px',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: 'auto',
        }}>
          <Badge
            status="warning"
            text={<Text style={{ fontSize: '14px', color: '#fa8c16' }}>Needs Attention</Text>}
            style={{ display: 'block', marginBottom: '4px' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {displayState.description}
          </Text>
        </div>

        {/* Action Zone (40px) */}
        <Button
          onClick={handleRetry}
          disabled={loading}
          block
          style={{
            height: '40px',
            marginTop: '12px',
            backgroundColor: '#fa8c16',
            borderColor: '#fa8c16',
            color: '#fff',
          }}
        >
          Reconnect Now
        </Button>
      </Card>
    )
  }

  // STATE: Paused (Expired) - Paused but credentials expired (orange)
  // User needs to reconnect before they can resume
  if (displayState && displayState.label === 'Paused (Expired)') {
    return (
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          height: '188px',
          borderColor: '#fa8c16',
        }}
        bodyStyle={{
          padding: '16px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Zone (48px) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '48px',
          marginBottom: '12px',
        }}>
          {renderIcon()}
          <Text strong style={{ fontSize: '16px', flex: 1 }}>
            {platformConfig.displayName}
          </Text>
          <ConnectionActionsMenu
            connection={connection}
            onAction={onAction}
            disabled={loading}
          />
        </div>

        {/* Status Zone (60px) */}
        <div style={{
          backgroundColor: 'var(--rp-temporal-orange-soft)',
          borderRadius: '4px',
          padding: '12px',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: 'auto',
        }}>
          <Badge
            status="warning"
            text={<Text style={{ fontSize: '14px', color: '#fa8c16' }}>Paused (Expired)</Text>}
            style={{ display: 'block', marginBottom: '4px' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {displayState.description}
          </Text>
        </div>

        {/* Action Zone (40px) */}
        <Button
          onClick={handleRetry}
          disabled={loading}
          block
          style={{
            height: '40px',
            marginTop: '12px',
            backgroundColor: '#fa8c16',
            borderColor: '#fa8c16',
            color: '#fff',
          }}
        >
          Reconnect to Resume
        </Button>
      </Card>
    )
  }

  // STATE: Paused - User intentionally paused syncing (yellow)
  if (displayState && displayState.label === 'Paused') {
    return (
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          height: '188px',
          borderColor: '#faad14',
        }}
        bodyStyle={{
          padding: '16px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Zone (48px) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '48px',
          marginBottom: '12px',
        }}>
          {renderIcon()}
          <Text strong style={{ fontSize: '16px', flex: 1 }}>
            {platformConfig.displayName}
          </Text>
          <ConnectionActionsMenu
            connection={connection}
            onAction={onAction}
            disabled={loading}
          />
        </div>

        {/* Status Zone (60px) */}
        <div style={{
          backgroundColor: 'var(--rp-temporal-orange-soft)',
          borderRadius: '4px',
          padding: '12px',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: 'auto',
        }}>
          <Badge
            status="warning"
            text={<Text style={{ fontSize: '14px', color: '#d48806' }}>Paused</Text>}
            style={{ display: 'block', marginBottom: '4px' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {displayState.description}
          </Text>
        </div>

        {/* Action Zone (40px) */}
        <Button
          onClick={() => onAction('unpause', connection)}
          disabled={loading}
          block
          style={{
            height: '40px',
            marginTop: '12px',
            backgroundColor: '#faad14',
            borderColor: '#faad14',
            color: '#fff',
          }}
        >
          Resume Syncing
        </Button>
      </Card>
    )
  }

  // STATE: Setup Incomplete - OAuth flow not completed (gray)
  if (displayState && displayState.label === 'Setup Incomplete') {
    return (
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          height: '188px',
        }}
        bodyStyle={{
          padding: '16px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Zone (48px) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '48px',
          marginBottom: '12px',
        }}>
          {renderIcon()}
          <Text strong style={{ fontSize: '16px', flex: 1 }}>
            {platformConfig.displayName}
          </Text>
          <ConnectionActionsMenu
            connection={connection}
            onAction={onAction}
            disabled={loading}
          />
        </div>

        {/* Status Zone (60px) */}
        <div style={{
          backgroundColor: 'var(--rp-surface-tertiary)',
          borderRadius: '4px',
          padding: '12px',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: 'auto',
        }}>
          <Badge
            status="default"
            text={<Text type="secondary" style={{ fontSize: '14px' }}>Setup Incomplete</Text>}
            style={{ display: 'block', marginBottom: '4px' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {displayState.description}
          </Text>
        </div>

        {/* Action Zone (40px) */}
        <Button
          type="primary"
          onClick={handleRetry}
          disabled={loading}
          block
          style={{ height: '40px', marginTop: '12px' }}
        >
          Complete Setup
        </Button>
      </Card>
    )
  }

  // STATE: Archived - Long-term inactive (gray)
  if (displayState && displayState.label === 'Archived') {
    return (
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          height: '188px',
          opacity: 0.7,
        }}
        bodyStyle={{
          padding: '16px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header Zone (48px) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          height: '48px',
          marginBottom: '12px',
        }}>
          {renderIcon()}
          <Text strong style={{ fontSize: '16px', flex: 1, color: '#8c8c8c' }}>
            {platformConfig.displayName}
          </Text>
          <ConnectionActionsMenu
            connection={connection}
            onAction={onAction}
            disabled={loading}
          />
        </div>

        {/* Status Zone (60px) */}
        <div style={{
          backgroundColor: 'var(--rp-surface-tertiary)',
          borderRadius: '4px',
          padding: '12px',
          height: '60px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginBottom: 'auto',
        }}>
          <Badge
            status="default"
            text={<Text type="secondary" style={{ fontSize: '14px' }}>Archived</Text>}
            style={{ display: 'block', marginBottom: '4px' }}
          />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {displayState.description}
          </Text>
        </div>

        {/* Action Zone (40px) */}
        <Button
          onClick={() => onAction('restore', connection)}
          disabled={loading}
          block
          style={{ height: '40px', marginTop: '12px' }}
        >
          Restore Connection
        </Button>
      </Card>
    )
  }

  // STATE: Connected - Active with valid/refreshing credentials (green/blue)
  // This is the fallback for any Connected state
  const isRefreshing = displayState && displayState.color === 'blue'

  return (
    <Card
      style={{
        borderRadius: '8px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        height: '188px',
      }}
      bodyStyle={{
        padding: '16px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Zone (48px) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        height: '48px',
        marginBottom: '12px',
      }}>
        {renderIcon()}
        <Text strong style={{ fontSize: '16px', flex: 1 }}>
          {platformConfig.displayName}
        </Text>
        <ConnectionActionsMenu
          connection={connection}
          onAction={onAction}
          disabled={loading}
        />
      </div>

      {/* Status Zone (60px) */}
      <div style={{
        backgroundColor: isRefreshing ? 'var(--rp-temporal-blue-soft)' : 'var(--rp-success-soft)',
        borderRadius: '4px',
        padding: '12px',
        height: '60px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginBottom: 'auto',
      }}>
        <Badge
          status={isRefreshing ? 'processing' : 'success'}
          text={
            <Text style={{ fontSize: '14px', color: isRefreshing ? 'var(--rp-blue-primary)' : 'var(--rp-success)' }}>
              {isRefreshing ? 'Refreshing...' : 'Connected'}
            </Text>
          }
          style={{ display: 'block', marginBottom: '4px' }}
        />
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {isRefreshing
            ? displayState?.description
            : `Last tested: ${formatDate(connection.lastTestedAt)}`}
        </Text>
      </div>

      {/* Action Zone (40px) */}
      <Button
        type="default"
        onClick={() => onAction('test', connection)}
        disabled={loading}
        block
        style={{ height: '40px', marginTop: '12px' }}
      >
        Test Connection
      </Button>
    </Card>
  )
}

export default PlatformSlot
